import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye, MapPin, Package, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import WarehouseService from '@/api/warehouses.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface WarehouseLocation {
  id: number;
  publicId: string;
  warehouseId: number;
  locationCode: string;
  name: string;
  description?: string;
  locationType: 'general' | 'receiving' | 'shipping' | 'quarantine';
  isActive: boolean;
  timezone?: string;
  dateCreated: string;
  dateUpdated?: string;
}

const WarehouseLocations: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [warehouses, setWarehouses] = useState<any[]>([]);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchWarehouses();
  }, [businessId]);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchLocations();
    }
  }, [selectedWarehouse]);

  const fetchWarehouses = async () => {
    try {
      const response = await WarehouseService.getAllWarehouses(businessId);
      console.log("getAllWarehouses response.data", response.data);
      
      if (response.data.result === 'success') {
        const warehouseList = response.data?.warehouses || [];
        setWarehouses(warehouseList);
        if (warehouseList.length > 0 && !selectedWarehouse) {
          setSelectedWarehouse(warehouseList[0].publicId);
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch warehouses",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast({
        title: "Error",
        description: "Failed to fetch warehouses",
        variant: "destructive",
      });
    }
  };

  const fetchLocations = async () => {
    if (!selectedWarehouse) return;
    
    try {
      setLoading(true);
      const response = await WarehouseService.getWarehouseLocations(selectedWarehouse);
      
      if (response.data.result === 'success') {
        setLocations(response.data?.locations || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch warehouse locations",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      toast({
        title: "Error",
        description: "Failed to fetch warehouse locations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (location: WarehouseLocation) => {
    if (!canDelete('Warehouse')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to delete locations",
        variant: "destructive",
      });
      return;
    }

    try {
      await WarehouseService.deleteWarehouseLocation(location.publicId);
      toast({
        title: "Success",
        description: "Location deleted successfully",
      });
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast({
        title: "Error",
        description: "Failed to delete location",
        variant: "destructive",
      });
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'receiving':
        return <Package className="h-4 w-4 text-blue-500" />;
      case 'shipping':
        return <Package className="h-4 w-4 text-green-500" />;
      case 'quarantine':
        return <Package className="h-4 w-4 text-red-500" />;
      case 'general':
      default:
        return <MapPin className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      general: { variant: 'default' as const, label: 'General' },
      receiving: { variant: 'secondary' as const, label: 'Receiving' },
      shipping: { variant: 'outline' as const, label: 'Shipping' },
      quarantine: { variant: 'destructive' as const, label: 'Quarantine' },
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.general;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.locationCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || location.locationType === typeFilter;
    return matchesSearch && matchesType;
  });

  const activeLocations = filteredLocations.filter(loc => loc.isActive).length;
  const selectedWarehouseName = warehouses.find(w => w.publicId === selectedWarehouse)?.name || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Warehouse Locations</h1>
          <p className="text-muted-foreground">
            Manage warehouse locations and storage areas
          </p>
        </div>
        {canCreate('Warehouse') && selectedWarehouse && (
          <Button onClick={() => navigate(`/warehouse/locations/create?warehouseId=${selectedWarehouse}`)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Location
          </Button>
        )}
      </div>

      {/* Warehouse Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Warehouse</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="px-3 py-2 border border-input rounded-md w-full max-w-md"
          >
            <option value="">Select a warehouse...</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.publicId} value={warehouse.publicId}>
                {warehouse.name} ({warehouse.code})
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {selectedWarehouse && (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Locations</CardTitle>
                <MapPin className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredLocations.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Locations</CardTitle>
                <Building className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeLocations}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Receiving Areas</CardTitle>
                <Package className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {filteredLocations.filter(loc => loc.locationType === 'receiving').length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Shipping Areas</CardTitle>
                <Package className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {filteredLocations.filter(loc => loc.locationType === 'shipping').length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Search & Filter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search locations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md"
                >
                  <option value="">All Types</option>
                  <option value="general">General</option>
                  <option value="receiving">Receiving</option>
                  <option value="shipping">Shipping</option>
                  <option value="quarantine">Quarantine</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Locations Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Warehouse Locations - {selectedWarehouseName} ({filteredLocations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Location Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLocations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8">
                            <div className="flex flex-col items-center gap-2">
                              <MapPin className="h-8 w-8 text-muted-foreground" />
                              <p className="text-muted-foreground">No warehouse locations found</p>
                              {canCreate('Warehouse') && (
                                <Button onClick={() => navigate(`/warehouse/locations/create?warehouseId=${selectedWarehouse}`)} size="sm">
                                  Create your first location
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLocations.map((location) => (
                          <TableRow key={location.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getTypeIcon(location.locationType)}
                                <div className="font-medium">{location.locationCode}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{location.name}</div>
                            </TableCell>
                            <TableCell>
                              {getTypeBadge(location.locationType)}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground max-w-xs truncate">
                                {location.description || 'No description'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={location.isActive ? "default" : "secondary"}>
                                {location.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {new Date(location.dateCreated).toLocaleDateString()}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Open menu</span>
                                    <div className="h-4 w-4">⋮</div>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  {canView('Warehouse') && (
                                    <DropdownMenuItem onClick={() => navigate(`/warehouse/locations/${location.publicId}`)}>
                                      <Eye className="mr-2 h-4 w-4" />
                                      View Details
                                    </DropdownMenuItem>
                                  )}
                                  {canEdit('Warehouse') && (
                                    <DropdownMenuItem onClick={() => navigate(`/warehouse/locations/${location.publicId}/edit`)}>
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Location
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => navigate(`/inventory/items?locationId=${location.publicId}`)}>
                                    <Package className="mr-2 h-4 w-4" />
                                    View Inventory
                                  </DropdownMenuItem>
                                  {canDelete('Warehouse') && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem 
                                        onClick={() => handleDeleteLocation(location)}
                                        className="text-red-600"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete Location
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default WarehouseLocations; 